// The trader score: one number from 0 to 100 for the last 30 trading days, made of six parts that
// each score 0–100 (profit factor, win size vs loss size, win rate against the break-even win rate,
// drawdown control, recovery and consistency), weighted and added up. Also: the score week by week,
// last month's score, the best month, and the one change that would lift the score the most,
// replayed on the trader's own trades. ScoreViews.js shows it.
import { netPnl, tradingDayKey } from './pnl';
import { translateNow as tn } from './langState';

export const WINDOW_DAYS = 30;
export const MIN_TRADES = 10;
export const MIN_DAYS = 5;

export const PARTS = [
  { id: 'pf', label: 'Profit factor', weight: 0.2 },
  { id: 'ratio', label: 'Win size vs loss size', short: 'Win/loss size', weight: 0.15 },
  { id: 'winRate', label: 'Win rate', weight: 0.15 },
  { id: 'drawdown', label: 'Drawdown control', short: 'Drawdown', weight: 0.2 },
  { id: 'recovery', label: 'Recovery', weight: 0.15 },
  { id: 'consistency', label: 'Consistency', weight: 0.15 },
];

// A straight line between points [[x, score], …], flat beyond the ends.
function scale(x, points) {
  if (x <= points[0][0]) return points[0][1];
  for (let i = 1; i < points.length; i += 1) {
    const [x1, y1] = points[i];
    const [x0, y0] = points[i - 1];
    if (x <= x1) return y0 + ((x - x0) / (x1 - x0)) * (y1 - y0);
  }
  return points[points.length - 1][1];
}

export function levelOf(score) {
  if (score >= 80) return 'Strong';
  if (score >= 60) return 'Steady';
  if (score >= 40) return 'Building';
  return 'Rebuilding';
}

// Each trade once, with its trading day worked out (the slow part) and its net, oldest first.
export function scoreRows(trades) {
  return trades.map((x) => ({ key: tradingDayKey(x.date), time: new Date(x.date).getTime(), net: netPnl(x) }))
    .sort((a, b) => a.time - b.time);
}

// The score for these rows (one window). null with too few trades or days.
export function scoreOf(rows) {
  if (rows.length < MIN_TRADES) return null;
  const dayMap = new Map();
  for (const r of rows) dayMap.set(r.key, (dayMap.get(r.key) || 0) + r.net);
  const days = [...dayMap.entries()].sort((a, b) => (a[0] < b[0] ? -1 : 1)).map(([key, net]) => ({ key, net }));
  if (days.length < MIN_DAYS) return null;
  const wins = rows.filter((r) => r.net > 0).map((r) => r.net);
  const losses = rows.filter((r) => r.net < 0).map((r) => -r.net);
  const grossWin = wins.reduce((s, n) => s + n, 0);
  const grossLoss = losses.reduce((s, n) => s + n, 0);
  const net = grossWin - grossLoss;
  const pf = grossLoss ? grossWin / grossLoss : grossWin ? Infinity : 0;
  const avgWin = wins.length ? grossWin / wins.length : 0;
  const avgLoss = losses.length ? grossLoss / losses.length : 0;
  const ratio = avgLoss ? avgWin / avgLoss : avgWin ? Infinity : 0;
  const winRate = (wins.length / rows.length) * 100;
  // The win rate that breaks even at this win size vs loss size.
  const breakEven = ratio === Infinity ? 0 : ratio ? 100 / (1 + ratio) : 100;
  // The deepest drop in the running P&L, day by day, from a high (starting at 0).
  let run = 0;
  let peak = 0;
  let maxDD = 0;
  for (const d of days) {
    run += d.net;
    peak = Math.max(peak, run);
    maxDD = Math.max(maxDD, peak - run);
  }
  const green = days.filter((d) => d.net > 0);
  const avgGreen = green.length ? green.reduce((s, d) => s + d.net, 0) / green.length : 0;
  const ddRatio = maxDD === 0 ? 0 : avgGreen ? maxDD / avgGreen : Infinity;
  const recovery = maxDD ? net / maxDD : net > 0 ? Infinity : 0;
  const greenPct = (green.length / days.length) * 100;
  const bestDay = green.length ? Math.max(...green.map((d) => d.net)) : 0;
  const bestShare = net > 0 ? (bestDay / net) * 100 : null;
  const greenScore = scale(greenPct, [[20, 0], [50, 50], [70, 85], [80, 100]]);

  const scores = {
    pf: pf === Infinity ? 100 : scale(pf, [[0.5, 0], [1, 40], [1.5, 75], [2.5, 100]]),
    ratio: ratio === Infinity ? 100 : scale(ratio, [[0.4, 0], [1, 45], [1.5, 70], [2.5, 100]]),
    winRate: scale(winRate - breakEven, [[-15, 0], [0, 45], [10, 75], [20, 100]]),
    drawdown: ddRatio === Infinity ? 0 : scale(ddRatio, [[0, 100], [2, 75], [4, 40], [8, 0]]),
    recovery: recovery === Infinity ? 100 : scale(recovery, [[-1, 0], [0, 30], [1, 60], [3, 100]]),
    // Green days, and (when the stretch is profitable) how much of the profit the best day made.
    consistency: bestShare === null ? greenScore : 0.6 * greenScore + 0.4 * scale(bestShare, [[20, 100], [40, 60], [80, 0]]),
  };
  const parts = PARTS.map((p) => ({ ...p, score: Math.round(scores[p.id]) }));
  const score = Math.round(PARTS.reduce((s, p) => s + scores[p.id] * p.weight, 0));
  return {
    score, parts, level: levelOf(score),
    stats: { trades: rows.length, days: days.length, from: days[0].key, to: days[days.length - 1].key, net, pf, ratio, avgWin, avgLoss, winRate, breakEven, maxDD, avgGreen, ddRatio, recovery, greenPct, greenDays: green.length, bestShare },
  };
}

// The rows of the last WINDOW_DAYS trading days up to and including endKey.
export function windowRows(rows, endKey) {
  const upTo = endKey ? rows.filter((r) => r.key <= endKey) : rows;
  const keys = [...new Set(upTo.map((r) => r.key))].slice(-WINDOW_DAYS);
  if (!keys.length) return [];
  return upTo.filter((r) => r.key >= keys[0]);
}

// Monday of the week a day key falls in.
function weekOf(key) {
  const d = new Date(`${key}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() - ((d.getUTCDay() + 6) % 7));
  return d.toISOString().slice(0, 10);
}

// Replays: the trades the trader would have kept with one rule.
function stopAtLoss(rows, cap) {
  const out = [];
  const run = new Map();
  for (const r of rows) {
    if ((run.get(r.key) || 0) <= -cap) continue;
    out.push(r);
    run.set(r.key, (run.get(r.key) || 0) + r.net);
  }
  return out;
}
function maxTrades(rows, n) {
  const count = new Map();
  return rows.filter((r) => {
    const c = (count.get(r.key) || 0) + 1;
    count.set(r.key, c);
    return c <= n;
  });
}
function stopAfterLosses(rows, n) {
  const streak = new Map();
  const out = [];
  for (const r of rows) {
    if ((streak.get(r.key) || 0) >= n) continue;
    out.push(r);
    streak.set(r.key, r.net < 0 ? (streak.get(r.key) || 0) + 1 : 0);
  }
  return out;
}
const roundTo = (n, step) => Math.max(step, Math.round(n / step) * step);
const dollars = (n) => `$${n.toLocaleString('en-US')}`;

// The one rule that lifts the score the most on these rows, or null when nothing lifts it 2+ points.
export function bestMove(rows, current) {
  if (!current) return null;
  const dayMap = new Map();
  const countMap = new Map();
  for (const r of rows) {
    dayMap.set(r.key, (dayMap.get(r.key) || 0) + r.net);
    countMap.set(r.key, (countMap.get(r.key) || 0) + 1);
  }
  const reds = [...dayMap.values()].filter((n) => n < 0).map((n) => -n).sort((a, b) => a - b);
  const counts = [...countMap.values()].sort((a, b) => a - b);
  const options = [];
  // Stop for the day at the typical red day.
  if (reds.length >= 2) {
    const mid = reds[Math.floor(reds.length / 2)];
    const cap = roundTo(mid, mid >= 500 ? 100 : 50);
    const hit = reds.filter((n) => n > cap).length;
    if (hit) {
      options.push({ id: 'dailyStop', amount: cap, days: hit, rows: stopAtLoss(rows, cap),
        text: tn('Stop for the day at −{amount}', { amount: dollars(cap) }),
        detail: tn('You lost more than {amount} on {n} of these days.', { amount: dollars(cap), n: hit }),
        rule: tn('When I’m down {amount} on the day, I stop trading.', { amount: dollars(cap) }) });
    }
  }
  // A daily trade limit a little above the usual day.
  if (counts.length >= MIN_DAYS) {
    const n = Math.max(2, counts[Math.floor(counts.length * 0.6)]);
    const hit = counts.filter((c) => c > n).length;
    if (hit) {
      options.push({ id: 'maxTrades', amount: n, days: hit, rows: maxTrades(rows, n),
        text: tn('No more than {n} trades a day', { n }),
        detail: tn('You took more than {n} trades on {d} of these days.', { n, d: hit }),
        rule: tn('No more than {n} trades a day.', { n }) });
    }
  }
  // Stop after 3 losses in a row.
  const after = stopAfterLosses(rows, 3);
  if (after.length < rows.length) {
    const kept = new Set(after);
    const hit = new Set(rows.filter((r) => !kept.has(r)).map((r) => r.key)).size;
    options.push({ id: 'threeLosses', amount: 3, days: hit, rows: after,
      text: tn('Stop after 3 losses in a row'),
      detail: tn('You kept trading after 3 losses in a row on {n} of these days.', { n: hit }),
      rule: tn('After 3 losing trades in a row, I stop for the day.') });
  }
  let best = null;
  for (const { rows: kept, ...o } of options) {
    const s = scoreOf(kept);
    if (!s || s.score - current.score < 2) continue;
    if (!best || s.score > best.after.score) {
      // The part that moved the most, to explain the lift.
      const moved = s.parts.map((p, i) => ({ ...p, before: current.parts[i].score })).sort((a, b) => (b.score - b.before) - (a.score - a.before))[0];
      best = { ...o, after: s, gain: s.score - current.score, moved };
    }
  }
  return best;
}

// Everything the score views show: now, week by week (up to 12 weeks), last month, the best month
// and the best move. null without trades; { now: null, need } with too few.
export function traderScore(trades) {
  const rows = scoreRows(trades);
  if (!rows.length) return null;
  const now = scoreOf(windowRows(rows));
  const keys = [...new Set(rows.map((r) => r.key))];
  if (!now) return { now: null, need: { trades: Math.max(0, MIN_TRADES - rows.length), days: Math.max(0, MIN_DAYS - keys.length) } };
  const last = keys[keys.length - 1];
  // The last trading day of each week, the latest 12 weeks.
  const weekEnds = new Map();
  for (const k of keys) weekEnds.set(weekOf(k), k);
  const weeks = [...weekEnds.entries()].slice(-12).map(([week, end]) => {
    const s = scoreOf(windowRows(rows, end));
    return s ? { week, end, score: s.score } : null;
  }).filter(Boolean);
  // Each month's score at its last trading day.
  const monthEnds = new Map();
  for (const k of keys) monthEnds.set(k.slice(0, 7), k);
  const months = [...monthEnds.entries()].map(([month, end]) => {
    const s = scoreOf(windowRows(rows, end));
    return s ? { month, end, score: s.score } : null;
  }).filter(Boolean);
  const lastMonth = months.filter((m) => m.month < last.slice(0, 7)).pop() || null;
  const best = months.reduce((b, m) => (!b || m.score > b.score ? m : b), null);
  return { now, weeks, lastMonth, best, move: bestMove(windowRows(rows), now) };
}
